import { useState, useEffect, useRef } from 'react';
import './multiSelect.css';

export default function MultiSelect({
    label = '',
    helperText = '',
    name = '',
    placeholder = 'Select types',
    data = [],
    value = [],
    max = null,
    error,
    search = false,
    selectAll = false,
    listAll = true,
    closeListOnItemSelect = false,
    dropdownWidth = '',
    dropdownHeight = '',
    onChange = () => {},
    onSelect = () => {},
    onUnselect = () => {}
}) {
    const [options, setOptions] = useState(data.map((item) => ({ ...item, selected: item.selected || false })));
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef(null);

    const selectedValues = options.filter((opt) => opt.selected).map((opt) => opt.value);

    const toggleOption = (value) => {
        setOptions((prev) => {
            const updatedOptions = prev.map((opt) => {
                if (opt.value === value) {
                    const newSelected = !opt.selected;
                    newSelected ? onSelect(opt.value, opt.text) : onUnselect(opt.value, opt.text);
                    return { ...opt, selected: newSelected };
                }
                return opt;
            });

            const selectedValues = updatedOptions.filter((opt) => opt.selected).map((opt) => opt.value);

            onChange(selectedValues);

            return updatedOptions;
        });

        if (closeListOnItemSelect) setDropdownOpen(false);
    };

    const handleSelectAll = () => {
        const allSelected = options.every((opt) => opt.selected);
        setOptions((prev) => prev.map((opt) => ({ ...opt, selected: !allSelected })));
    };

    useEffect(() => {
        setOptions(data.map((item) => ({ ...item, selected: item.selected || false })));
    }, [data]);

    useEffect(() => {
        setOptions(
            data.map((item) => ({
                ...item,
                selected: value.includes(item.value)
            }))
        );
    }, [data, value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = options.filter((opt) => opt?.text?.toLowerCase().includes(searchTerm?.toLowerCase()));

    return (
        <div className='dropRegDiv'>
            <label className='dropRegLabel'>{label}</label>
            <div className={`multi-select `} style={{ width: dropdownWidth || 'auto' }} ref={wrapperRef}>
                {selectedValues.map((val) => (
                    <input key={val} type='hidden' name={`${name}[]`} value={val} />
                ))}

                <div
                    className={`multi-select-header ${error ? 'error-highlight' : ''} ${dropdownOpen ? 'multi-select-header-active' : ''}`}
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    style={{ width: dropdownWidth }}
                >
                    <span className='multi-select-header-max'>{max ? `${selectedValues.length}/${max}` : ''}</span>
                    {selectedValues.length > 0 ? (
                        listAll ? (
                            options
                                .filter((opt) => opt.selected)
                                .map((opt) => (
                                    <span key={opt.value} className='multi-select-header-option'>
                                        {opt.text}
                                    </span>
                                ))
                        ) : (
                            <span className='multi-select-header-option'>{selectedValues.length} selected</span>
                        )
                    ) : (
                        <span className='multi-select-header-placeholder'>{placeholder}</span>
                    )}
                </div>

                {dropdownOpen && (
                    <div className={`multi-select-options `} style={{ width: dropdownWidth, height: dropdownHeight }}>
                        {search && (
                            <input
                                type='text'
                                className='multi-select-search'
                                placeholder='Search...'
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        )}

                        {selectAll && (
                            <div className='multi-select-option multi-select-all' onClick={handleSelectAll}>
                                <span className='multi-select-option-radio'></span>
                                <span className='multi-select-option-text'>Select all</span>
                            </div>
                        )}

                        {filteredOptions.map((opt) => (
                            <div
                                key={opt.value}
                                className={`multi-select-option ${opt.selected ? 'multi-select-selected' : ''}`}
                                onClick={() => {
                                    if (!opt.selected && max && selectedValues.length >= max) return;
                                    toggleOption(opt.value);
                                }}
                            >
                                <span className='multi-select-option-radio'></span>
                                <span className='multi-select-option-text'>
                                    {opt.html ? <span dangerouslySetInnerHTML={{ __html: opt.html }} /> : opt.text}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
                {helperText && <span className='helperText'>{helperText}</span>}
            </div>
        </div>
    );
}
